/**
 * Slack Settings API helpers
 */

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Helper to get admin token (for MVP - should be replaced with session auth)
const getAdminToken = () => 'dev';

/**
 * Get Slack integration status for a brand
 */
export async function getSlackStatus(brandId) {
  const res = await fetch(
    `${API_BASE}/api/v1/integrations/slack/status?brand_id=${encodeURIComponent(brandId)}`,
    {
      headers: {
        'X-Admin-Token': getAdminToken()
      }
    }
  );
  
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || 'Failed to get Slack status');
  }
  
  return res.json();
}

/**
 * Register a Slack workspace
 */
export async function registerSlackWorkspace({ teamId, brandId, botToken, teamName }) {
  const res = await fetch(
    `${API_BASE}/api/v1/integrations/slack/workspaces/register`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Admin-Token': getAdminToken()
      },
      body: JSON.stringify({
        team_id: teamId,
        brand_id: brandId,
        bot_token: botToken,
        team_name: teamName || null
      })
    }
  );
  
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || 'Failed to register workspace');
  }
  
  return res.json();
}

/**
 * Fetch available Slack channels
 */
export async function fetchSlackChannels(teamId) {
  const res = await fetch(
    `${API_BASE}/api/v1/integrations/slack/workspaces/channels?team_id=${encodeURIComponent(teamId)}`,
    {
      headers: {
        'X-Admin-Token': getAdminToken()
      }
    }
  );
  
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || 'Failed to fetch channels');
  }
  
  return res.json();
}

/**
 * Set the default approval channel
 */
export async function setDefaultChannel({ teamId, brandId, channelId, channelName }) {
  const res = await fetch(
    `${API_BASE}/api/v1/integrations/slack/workspaces/set-default-channel`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Admin-Token': getAdminToken()
      },
      body: JSON.stringify({
        team_id: teamId,
        brand_id: brandId,
        channel_id: channelId,
        channel_name: channelName || null
      })
    }
  );
  
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || 'Failed to set default channel');
  }
  
  return res.json();
}

/**
 * Request Slack approval for a decision trace
 */
export async function requestSlackApproval({ teamId, brandId, traceId, channelId, summary }) {
  const res = await fetch(
    `${API_BASE}/api/v1/integrations/slack/send-approval`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Admin-Token': getAdminToken()
      },
      body: JSON.stringify({
        team_id: teamId || undefined,
        brand_id: brandId,
        trace_id: traceId,
        channel_id: channelId || undefined,
        summary: summary
      })
    }
  );
  
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || 'Failed to send Slack approval request');
  }
  
  return res.json();
}

/**
 * Test Slack connection by posting a test message
 */
export async function testSlackPost({ teamId, brandId, channelId }) {
  return requestSlackApproval({
    teamId,
    brandId,
    traceId: 'dt_test',
    channelId,
    summary: '*CI Slack test:* If you see this, approvals are wired correctly! \u2705'
  });
}
